/**
 * 부서별 월간보고서 API (v2.280.0 · 2026-10-19)
 *
 * ⭐ 목록·상세·결재(승인·반려·회수·삭제)는 **지출결재와 같은 통로**(expense-api)를 그대로 쓴다.
 *   여기 있는 것은 「부서별 월간보고서에만 있는 것」 — 내 소속 판정·중복 확인·작성·수정뿐이다.
 * ⭐작성자 표기(소속·직급·성명)는 서버가 세션 계정으로 채운다 — 화면은 보내지 않는다(남의 명의 방지).
 */

import type { ApiResponse } from './types';

const BASE = '/api';

/** 양식 표의 한 줄 — 구분(세로 병합되는 묶음) · 【키워드】 · 내용 · 비고 */
export interface DeptReportRow {
  category: string;
  keyword: string;
  content: string;
  note: string;
}

/** 서버 zod 와 같은 상한 — 화면은 안내만, 막는 것은 서버 */
export const DEPT_REPORT_LIMITS = { category: 30, keyword: 40, content: 600, note: 120, rows: 80 } as const;

/**
 * [📝 보고서 작성] 단추가 할 일 — 서버가 **소속·팀**으로 정한다 (GET /expense/dept-report/me).
 *   form = 경영지원부·기업부설연구소(여기서 작성) · sales = 시스템사업부(매출 분석 창) · tsp = 기술지원팀(안내만) · none = 소속 없음
 */
export type DeptReportMode = 'form' | 'sales' | 'tsp' | 'none';

export interface DeptReportMe {
  mode: DeptReportMode;
  dept: string | null;
  team: string | null;
  name: string;
  rank: string;
  /** 이 문서를 쓰는 부서면 그 이름(지금 이름) · 아니면 null */
  deptKey: string | null;
  /** 처음 쓸 때 미리 깔아 주는 「구분」 */
  defaultCategories: string[];
  /** 올릴 수 있는 첫 달 'YYYY-MM' */
  startYm: string;
  /** 작성 창의 기본 대상 월 — 지난달 */
  suggestedPeriodYm: string;
  /** 가장 최근 보고서 — [지난 보고서 가져오기] */
  latest: {
    docId: number; docNo: string; status: string; periodYm: string;
    prevRows: DeptReportRow[]; curRows: DeptReportRow[];
  } | null;
}

/** 그 달을 지금 올릴 수 있나 (GET /expense/dept-report/check) */
export interface DeptReportCheck {
  periodYm: string;
  canSubmit: boolean;
  /** 못 올리는 이유 (서버가 이미 한국어로 쓴다 — 그대로 보여 준다) */
  reason: string | null;
  /** 이미 있는 그 달 문서 — 있으면 새로 만드는 대신 그 문서로 보낸다 */
  existing: { docId: number; docNo: string; status: string } | null;
}

/** 상세(GET /expense/docs/:id)의 deptReport 칸 — 다른 종류면 null */
export interface DeptReportBody {
  /** 보고 부서(지금 이름) */
  dept: string;
  /** 대상 월 'YYYY-MM' — 양식의 「2. 전월」. 「3. 당월」은 그 다음 달 */
  periodYm: string;
  author: { dept: string; rank: string; name: string };
  prevRows: DeptReportRow[];
  curRows: DeptReportRow[];
  createdAt: string | null;
  updatedAt: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  const json = (await res.json()) as ApiResponse<T>;
  // 서버가 이미 사람이 읽을 한국어 문구를 준다 — 여기서 갈아 끼우지 않는다
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

async function send<T>(path: string, method: 'POST' | 'PUT', body: unknown): Promise<T> {
  return call<T>(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getDeptReportMe(): Promise<DeptReportMe> {
  return call('/expense/dept-report/me');
}

/**
 * [보고서 작성] 을 누르기 전 확인.
 * ⚠이건 **안내용**이다 — 실제 중복 차단은 서버(POST)와 DB 가 한다.
 */
export async function checkDeptReport(year: number, month: number): Promise<DeptReportCheck> {
  const qs = new URLSearchParams({ year: String(year), month: String(month) });
  return call(`/expense/dept-report/check?${qs.toString()}`);
}

/** 작성 — submit=false 면 임시저장(작성중), true 면 그 자리에서 상신(검토대기) */
export async function createDeptReport(input: {
  year: number;
  month: number;
  prevRows: DeptReportRow[];
  curRows: DeptReportRow[];
  submit: boolean;
}): Promise<{ docId: number; docNo: string; status: string; periodYm: string }> {
  return send('/expense/dept-report', 'POST', input);
}

/**
 * 수정 — 작성자 본인의 작성중·반려 문서만.
 * ⭐대상 월은 못 바꾼다(부서마다 한 달에 한 건). 다른 달을 올리려면 새로 만든다.
 */
export async function updateDeptReport(
  docId: number,
  input: { prevRows: DeptReportRow[]; curRows: DeptReportRow[]; submit: boolean },
): Promise<{ docId: number; docNo: string; status: string }> {
  return send(`/expense/dept-report/${docId}`, 'PUT', input);
}

/**
 * [📄 미리보기] — 저장 없이 지금 적힌 줄로 확정본과 같은 PDF (v2.281.0). Blob URL 을 돌려준다(호출부가 미리보기 창으로 연다).
 * ⭐지출결재의 previewExpensePdf 와 같은 모양 — 서버가 PDF 대신 오류를 주면 그 한국어 문구를 그대로 던진다.
 */
export async function previewDeptReportPdf(input: {
  year: number;
  month: number;
  prevRows: DeptReportRow[];
  curRows: DeptReportRow[];
}): Promise<string> {
  const res = await fetch(`${BASE}/expense/dept-report/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(json?.error?.message ?? '미리보기 생성 실패');
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** '2026-09' → '2026년 9월'. 형식이 아니면 받은 값 그대로 */
export function deptPeriodText(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  return m ? `${m[1]}년 ${Number(m[2])}월` : ym;
}

/** '2026-09' → '2026-10' — 「3. 당월」이 가리키는 달 */
export function nextPeriodYm(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
}

/** 이번 달 'YYYY-MM' */
export function thisYm(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** startYm 부터 이번 달까지 'YYYY-MM' 목록 — 최근 달이 앞 (작성 창의 대상 월 고르기) */
export function periodOptions(startYm: string, now = new Date()): string[] {
  const out: string[] = [];
  const end = thisYm(now);
  let cur = startYm;
  let guard = 0;
  while (cur <= end && guard < 120) {
    out.push(cur);
    cur = nextPeriodYm(cur);
    guard += 1;
  }
  return out.reverse();
}
